import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory2 as InventoryIcon,
  AddShoppingCart as AddShoppingCartIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FullImageDialog from '../common/FullImageDialog';
import { formatCurrency } from '../../utils/currencyUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { formatStockQuantity } from '../../utils/stockQuantityUtils';
import { getStockStatus, getStockStatusChipSx } from '../../utils/stockStatusUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const ProductCard = ({
  product,
  currency,
  loading,
  canEdit,
  canDelete,
  canPurchase,
  onEdit,
  onDelete,
  onPurchase,
}) => {
  const { t, i18n } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const localizedName = getLocalizedValue(product.name, i18n.language, undefined, '');
  const localizedDescription = getLocalizedValue(
    product.description,
    i18n.language,
    undefined,
    '',
  );
  const isActive = !product.disabled;
  const isRetail = product.isRetail !== false;
  const hasActions = canEdit || canDelete;
  const stockStatus = getStockStatus(product);
  const onHandLabel = formatStockQuantity({
    stockOnHand: product.stockOnHand,
    contentQuantity: product.contentQuantity,
    stockUnit: product.stockUnit,
  });
  const stockLabelKey = stockStatus === 'negative' || stockStatus === 'out'
    ? 'productsManagement.card.outOfStock'
    : stockStatus === 'low'
      ? 'productsManagement.card.lowStock'
      : 'productsManagement.card.stock';
  const showPurchase = Boolean(canPurchase);

  const renderActionButtons = () => (
    <>
      {canEdit ? (
        <Tooltip title={t('productsManagement.actions.edit')}>
          <IconButton size="small" onClick={onEdit} disabled={loading} sx={{ ...actionButtonSx, color: BRAND.accentText }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {canDelete ? (
        <Tooltip title={t('productsManagement.actions.delete')}>
          <IconButton size="small" onClick={onDelete} disabled={loading} sx={{ ...actionButtonSx, color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </>
  );

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
            {product.imageUrl ? (
              <Box
                component="img"
                src={product.imageUrl}
                alt=""
                onClick={() => setPreviewOpen(true)}
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: 'cover',
                  borderRadius: 1,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 1,
                  backgroundColor: BRAND.background,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: BRAND.accentText,
                }}
              >
                <InventoryIcon />
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, wordBreak: 'break-word', lineHeight: 1.3 }}>
                {localizedName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {localizedDescription || t('productsManagement.card.noDescription')}
              </Typography>
            </Box>
        </Box>

        <Box sx={{ mt: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: hasActions ? 0.75 : 0 }}>
            <Chip
              size="small"
              label={isActive ? t('productsManagement.status.active') : t('productsManagement.status.inactive')}
              sx={{
                fontWeight: 600,
                backgroundColor: isActive ? BRAND.accent : brandAlpha('text', 0.08),
                color: isActive ? BRAND.onAccent : BRAND.text,
                border: isActive ? 'none' : `1px solid ${BRAND.line}`,
              }}
            />
            {isRetail ? null : (
              <Chip size="small" label={t('productsManagement.card.internalUse')} />
            )}
            <Chip
              size="small"
              label={t(stockLabelKey)}
              sx={getStockStatusChipSx(stockStatus)}
            />
        </Box>

        {hasActions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {renderActionButtons()}
          </Box>
        ) : null}
        </Box>

        <Box
          sx={{
            pt: 1.5,
            mt: 1.5,
            borderTop: `1px solid ${BRAND.line}`,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {isRetail ? t('productsManagement.card.price') : t('productsManagement.card.internalUse')}
            </Typography>
            {isRetail ? (
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatCurrency(product.price, currency)}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('productsManagement.card.onHand', { quantity: onHandLabel })}
            </Typography>
          </Box>
          {showPurchase ? (
            <Tooltip title={t('productsManagement.actions.purchaseMore')}>
              <IconButton
                size="small"
                onClick={onPurchase}
                disabled={loading}
                aria-label={t('productsManagement.actions.purchaseMore')}
                sx={{ ...actionButtonSx, color: BRAND.accentText }}
              >
                <AddShoppingCartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </CardContent>

      <FullImageDialog
        open={previewOpen}
        imageUrl={product.imageUrl}
        alt={localizedName}
        onClose={() => setPreviewOpen(false)}
      />
    </Card>
  );
};

export default ProductCard;
